import {ClaudeRun, ClaudeRunStatus, RunStats} from './api';

/**
 * Display helpers for the admin "Claude runs" page. The row model is the DDB row as the API
 * returns it (`functions/shared/claude-runs-repo.ts`); nothing here derives state, it only
 * formats. Rows record the launch decision, not the run: how long a session lasted and how it
 * ended is in CloudWatch, one click away through `cloudWatchLogUrl`.
 */

export const RUN_STATUS_LABEL: Record<ClaudeRunStatus, string> = {
    denied: 'Denied',
    spawning: 'Starting',
    spawned: 'Started',
    failed: 'Failed',
};

/** Maps onto the existing `.status-*` pill classes so the page speaks the same colour language. */
export function runStatusClass(status: ClaudeRunStatus): string {
    switch (status) {
        case 'denied':
        case 'failed':
            return 'status-denied';
        case 'spawned':
            return 'status-approved';
        default:
            return 'status-synced';
    }
}

/**
 * CloudWatch console deep link for the task's log stream in INFRA. The console wants the
 * group and stream names URL-encoded twice (`/` → `%2F` → `$252F`).
 */
export function cloudWatchLogUrl(run: ClaudeRun, region = 'us-east-1'): string | undefined {
    if (!run.logGroup || !run.logStream) {
        return undefined;
    }
    const enc = (s: string) => encodeURIComponent(s).replace(/%/g, '$25');
    return (
        `https://${region}.console.aws.amazon.com/cloudwatch/home?region=${region}` +
        `#logsV2:log-groups/log-group/${enc(run.logGroup)}/log-events/${enc(run.logStream)}`
    );
}

/** `repo @ branch`, or just the repo when the revision is the default branch. */
export function repoLabel(run: ClaudeRun): string {
    if (!run.repo) {
        return '—';
    }
    return run.revision ? `${run.repo} @ ${run.revision}` : run.repo;
}

/** Short task id for the table: the last 8 characters of the ECS task id. */
export function shortTaskId(run: ClaudeRun): string | undefined {
    return run.taskId ? run.taskId.slice(-8) : undefined;
}

/** `1h 05m`, `12m`, `<1m`. */
export function formatDuration(ms: number | undefined): string | undefined {
    if (ms === undefined || !Number.isFinite(ms) || ms < 0) {
        return undefined;
    }
    const minutes = Math.round(ms / 60_000);
    if (minutes < 1) {
        return '<1m';
    }
    if (minutes < 60) {
        return `${minutes}m`;
    }
    return `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, '0')}m`;
}

/** `$2.96`, `$0.04`, `<$0.01`. */
export function formatUsd(usd: number | undefined): string | undefined {
    if (usd === undefined || !Number.isFinite(usd)) {
        return undefined;
    }
    if (usd > 0 && usd < 0.01) {
        return '<$0.01';
    }
    return `$${usd.toFixed(2)}`;
}

/** Claude + AWS, when at least one is known. */
export function totalUsd(stats: RunStats | undefined): number | undefined {
    if (!stats || (stats.claudeUsd === undefined && stats.awsUsd === undefined)) {
        return undefined;
    }
    return (stats.claudeUsd ?? 0) + (stats.awsUsd ?? 0);
}

/** `5.5M` / `31k` / `870`. */
export function formatTokens(n: number): string {
    if (n >= 1_000_000) {
        return `${(n / 1_000_000).toFixed(1)}M`;
    }
    if (n >= 1_000) {
        return `${Math.round(n / 1_000)}k`;
    }
    return String(n);
}

/** Tooltip for the cost cell: the per-model token breakdown the estimate is built from. */
export function usageTooltip(stats: RunStats): string {
    const lines = stats.usage.map(u => {
        const write = u.cacheWrite5m + u.cacheWrite1h;
        const speed = u.speed === 'standard' ? '' : ` (${u.speed})`;
        return (
            `${u.model}${speed}: ${formatTokens(u.input)} in, ${formatTokens(u.output)} out, ` +
            `${formatTokens(u.cacheRead)} cache read, ${formatTokens(write)} cache write`
        );
    });
    lines.push('Claude: API list-price equivalent; AWS: on-demand compute for the task runtime.');
    if (stats.unpricedModels.length > 0) {
        lines.push(`No price for: ${stats.unpricedModels.join(', ')}`);
    }
    return lines.join('\n');
}
